import React, { useRef, useState, useEffect, useCallback, useMemo } from 'react';
import { connect } from 'react-redux';
import { useFormik } from 'formik';
import * as Yup from 'yup';

import { Modal, ModalBodyFooter, Button, ModalProcessing, ErrorMessage } from '../../custom-essentials';
import { formatDateApi, formatDate } from '../../functions';
import { getDataArray } from '../../functions/getDataArray';
import { DatePicker, SelectSingle, FormikControl, Check } from '../../form';
import { checkResponse } from '../../form';

import {
    fetchContractsIds,
    fetchPaymentsIds,
    fetchContractsStudentNames,
    fetchContractsStudentIds,
    createPayment,
    updatePayment,
    deletePayment
} from '../../../actions';

const initDate = formatDateApi(new Date());
const defaultContractOption = { value: -1, label: 'Please select... (search to view more items)' };
const methodOptions = ['Credit', 'Venmo', 'Zelle', 'Other'].map(i => ({ value: i, label: i }));

function getInitMethodOption(type) {
    if (!type) return { value: -1, label: 'Please select...' };
    return methodOptions.find(t => t.value === type) || { value: -1, label: `Unknown type (${type})` };
}

const validationSchema = Yup.object().shape({
    date: Yup.mixed().test(
        'dateIsValid',
        'Invalid Date',
        (value) => !isNaN(new Date(value).getTime())
    ),
    contract: Yup.mixed().test(
        'contractIsSelected',
        'Please select a valid contract',
        (value) => value && value.value && value.value !== -1
    ),
    method: Yup.mixed().test(
        'methodIsSelected',
        'Please select a valid method',
        (value) => value && value.value && value.value !== -1
    ),
    amount: Yup.number()
        .typeError('Value must be a number')
        .min(-30000, 'Min -30000')
        .max(30000, 'Max 30000')
        .integer('Whole Number Required')
        .required('Amount is required'),
    notes: Yup.string()
        .max(500, 'Max 500 characters'),
});

function MathPlexPaymentsModal(props) {
    const mounted = useRef(false);
    useEffect(() => {
        mounted.current = true;
        return () => (mounted.current = false);
    });

    const [submitted, setSubmitted] = useState(false);
    const [payment, setPayment] = useState(null);
    const [contractOption, setContractOption] = useState(null);
    const [fetching, setFetching] = useState(false);
    const [contractOptions, setContractOptions] = useState([]);
    const [hasFetched, setHasFetched] = useState(false);

    const {
        show = true,
        mode = 'create',
        payment: paymentProp,
        students = [],
        onClose,
        fetchPaymentsIds,
        fetchContractsIds,
        fetchContractsStudentNames,
        fetchContractsStudentIds,
        createPayment,
        updatePayment,
        deletePayment
    } = props;

    // Build student options
    const studentOptions = useMemo(() => {
        const opts = [{ value: -1, label: 'Please select a student...' }];
        if (Array.isArray(students)) {
            students.forEach(s => {
                if (s && s.user_id && s.first_name && s.last_name) {
                    opts.push({
                        value: s.user_id,
                        label: `${s.first_name} ${s.last_name}`.trim()
                    });
                }
            });
        }
        return opts;
    }, [students]);

    const handleClose = useCallback((shouldRefresh = false) => {
        if (mounted.current) setSubmitted(false);
        if (onClose) onClose(shouldRefresh);
    }, [onClose]);

    const handleSubmit = useCallback((values, actions) => {
        async function submit() {
            const { setStatus, setSubmitting } = actions;

            if (mounted.current) setSubmitting(true);

            let response = { status: 999 };

            if (['create', 'edit'].includes(mode)) {
                const paymentObject = {
                    date: values.date,
                    contract: values.contract.value,
                    method: values.method.value,
                    amount: values.amount,
                    notes: values.notes
                };

                if (mode === 'create') {
                    response = await createPayment(paymentObject);
                } else if (mode === 'edit') {
                    paymentObject.id = payment?.id;
                    response = await updatePayment(paymentObject);
                }
            } else if (mode === 'delete') {
                response = await deletePayment({ id: payment?.id });
            }

            const responseValid = checkResponse(response, mounted, setStatus);
            if (!responseValid && mounted.current) {
                setSubmitting(false);
                return;
            }

            if (mounted.current) setSubmitted(true);
            setTimeout(() => handleClose(true), 1000);
        }
        submit();
    }, [mode, handleClose, payment, createPayment, updatePayment, deletePayment]);

    // Get initial values
    const getInitialValues = () => {
        // For edit mode, get the student from the contract
        let initialStudent = { value: -1, label: 'Please select a student...' };
        if (mode === 'edit' && payment && contractOption) {
            // Try to find student from the contract (will be set in useEffect)
            initialStudent = { value: -1, label: 'Please select a student...' };
        }

        return {
            student: initialStudent,
            onlyShowActiveAndExtra: true,
            contract: contractOption || defaultContractOption,
            date: payment?.date ? formatDateApi(payment.date) : initDate,
            method: getInitMethodOption(payment?.method),
            amount: payment?.amount || 0,
            notes: payment?.notes || '',
        };
    };

    // Hooks must be called before any conditional returns
    const formik = useFormik({
        enableReinitialize: true,
        initialValues: getInitialValues(),
        validationSchema: mode === 'delete' ? Yup.object({}) : validationSchema,
        onSubmit: handleSubmit
    });

    // Fetch contracts for a selected student
    const fetchContractsForStudent = useCallback((studentId, activeExtraOnly) => {
        (async function () {
            if (!fetchContractsStudentIds) {
                if (mounted.current) setFetching(false);
                return;
            }
            if (!studentId || studentId === -1) {
                if (mounted.current) {
                    setContractOptions([]);
                    setFetching(false);
                    setHasFetched(false);
                }
                return;
            }

            if (mounted.current) setFetching(true);

            try {
                const contractsRes = await fetchContractsStudentIds({
                    studentIds: [studentId]
                });

                // Check response status - allow 200 and 201
                if (!contractsRes || (contractsRes.status !== 200 && contractsRes.status !== 201)) {
                    if (mounted.current) {
                        setFetching(false);
                        setHasFetched(true);
                        setContractOptions([]);
                    }
                    return;
                }

                // Extract contracts array - handle various response formats
                let newContracts = [];
                if (Array.isArray(contractsRes.data)) {
                    newContracts = contractsRes.data;
                } else if (contractsRes.data && Array.isArray(contractsRes.data.data)) {
                    newContracts = contractsRes.data.data;
                } else if (contractsRes.data && contractsRes.data.contracts && Array.isArray(contractsRes.data.contracts)) {
                    newContracts = contractsRes.data.contracts;
                } else {
                    newContracts = getDataArray(contractsRes);
                }

                // Ensure we have an array
                if (!Array.isArray(newContracts)) {
                    newContracts = [];
                }

                // Filter by activeExtraOnly if needed
                if (activeExtraOnly && newContracts.length > 0) {
                    // Get current contract ID for this student
                    const selectedStudent = students.find(s => s.user_id === studentId);
                    const currentContractField = window.location.hostname.includes('readingplex') ? 'rp_current_contract' : 'mp_current_contract';
                    const currentContractId = selectedStudent?.[currentContractField];

                    const filteredContracts = newContracts.filter(c => {
                        // Include current contract or Extra type contracts
                        return parseInt(c.id) === parseInt(currentContractId) || c.type === 'Extra';
                    });

                    // Only apply filter if it returns results, otherwise show all contracts
                    if (filteredContracts.length > 0) {
                        newContracts = filteredContracts;
                    }
                }

                if (newContracts.length === 0) {
                    if (mounted.current) {
                        setContractOptions([]);
                        setFetching(false);
                        setHasFetched(true);
                    }
                    return;
                }

                const selectedStudent = students.find(s => s.user_id === studentId) || {};
                const studentName = `${selectedStudent.first_name || ''} ${selectedStudent.last_name || ''}`.trim() || `Unknown student (UID: ${studentId})`;
                const currentContractField = window.location.hostname.includes('readingplex') ? 'rp_current_contract' : 'mp_current_contract';
                const currentContractId = selectedStudent?.[currentContractField];

                const newContractOptions = newContracts.sort((a, b) => {
                    const aDate = new Date(a.start_date);
                    const bDate = new Date(b.start_date);
                    return bDate - aDate;
                }).map(c => {
                    const isCurrent = parseInt(c.id) === parseInt(currentContractId);
                    const prefix = isCurrent ? '★ ' : (parseInt(c.id) === parseInt(contractOption?.value) ? '*' : '');
                    return ({
                        value: c.id,
                        label: `${prefix}${formatDate(c.start_date)} to ${formatDate(c.end_date)}: ${studentName} (${c.type})`,
                        isCurrent: isCurrent
                    });
                });

                // Pre-select current contract if available
                let defaultContract = defaultContractOption;
                const currentContractOption = newContractOptions.find(opt => opt.isCurrent);
                if (currentContractOption) {
                    defaultContract = currentContractOption;
                } else if (newContractOptions.length > 0) {
                    // If no current contract, select the most recent one
                    defaultContract = newContractOptions[0];
                }

                if (mounted.current) {
                    setContractOptions(newContractOptions);
                    setContractOption(defaultContract);
                    formik.setFieldValue('contract', defaultContract);
                    setFetching(false);
                    setHasFetched(true);
                }
            } catch (error) {
                if (mounted.current) {
                    setFetching(false);
                    setHasFetched(false);
                }
            }
        })();
    }, [mounted, students, contractOption, fetchContractsStudentIds, formik]);

    // Handle student selection changes - fetch contracts when student is selected
    useEffect(() => {
        const currentStudent = formik.values.student;
        const studentId = currentStudent?.value;
        if (studentId && studentId !== -1) {
            // Clear contract selection when student changes
            setContractOption(null);
            setContractOptions([]);
            setHasFetched(false);
            // Fetch contracts for selected student
            fetchContractsForStudent(studentId, formik.values.onlyShowActiveAndExtra);
        } else {
            // Clear contracts if no student selected
            setContractOptions([]);
            setContractOption(null);
            formik.setFieldValue('contract', defaultContractOption);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formik.values.student?.value, formik.values.onlyShowActiveAndExtra]);

    // Initialize payment and contract data when mode is edit
    useEffect(() => {
        async function init() {
            if (mode !== 'create' && paymentProp?.id) {
                const paymentRes = await fetchPaymentsIds({ ids: [paymentProp.id] });
                const contractRes = await fetchContractsIds({ ids: [paymentProp.contract] });
                const newPayment = paymentRes.data?.[0] || {};
                const newContract = contractRes.data?.[0] || {};

                const relStudent = students.find(s => s.user_id === newContract.student) || {};
                const studentName = `${relStudent.first_name || ''} ${relStudent.last_name || ''}`.trim()
                    || `Unknown student (UID: ${newContract.student})`;
                const newContractOption = {
                    value: parseInt(newContract.id),
                    label: `*${formatDate(newContract.start_date)} to ${formatDate(newContract.end_date)}: ${studentName}`
                };

                // Set student option for edit mode
                const studentOption = studentOptions.find(s => s.value === newContract.student) || { value: -1, label: 'Please select a student...' };

                if (mounted.current) {
                    setPayment(newPayment);
                    setContractOption(newContractOption);
                    // Set form values - will trigger fetchContractsForStudent via useEffect
                    formik.setFieldValue('student', studentOption);
                    formik.setFieldValue('contract', newContractOption);
                }
            } else if (mode === 'create') {
                if (mounted.current) {
                    setPayment(null);
                    setContractOption(null);
                }
            }
        }
        init();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mode, paymentProp?.id, studentOptions]);

    if (!show) return null;

    if (submitted) {
        return (
            <Modal show={show} onHide={() => handleClose(false)}>
                <Modal.Header><h2>{mode === 'create' ? 'New Payment' : mode === 'edit' ? 'Edit Payment' : 'Delete Payment'}</h2></Modal.Header>
                <ModalBodyFooter>
                    <Modal.Body>
                        <h4>Updated successfully!</h4>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button color="lte-mpLRed" onClick={handleClose}>Close</Button>
                    </Modal.Footer>
                </ModalBodyFooter>
            </Modal>
        );
    }

    if (mode === 'delete') {
        return (
            <Modal show={show} onHide={() => handleClose(false)}>
                <Modal.Header><h2>Delete Payment</h2></Modal.Header>
                <ModalBodyFooter>
                    <Modal.Body>
                        <h4>Delete this payment?</h4>
                        <div>Linked Contract: {contractOption?.label || 'Unknown'}</div>
                    </Modal.Body>
                    <Modal.Footer>
                        <div className="flex flex-row gap-x-2 flex-wrap">
                            {formik.isSubmitting && <ModalProcessing />}
                            {formik.status && !formik.isSubmitting &&
                                <div className="text-mpLRed text-xs">{formik.status}</div>
                            }
                            <Button color="lte-mpLRed" disabled={formik.isSubmitting} onClick={handleClose}>Cancel</Button>
                            <Button color="hol-mpLRed" disabled={formik.isSubmitting} onClick={formik.handleSubmit}>Delete</Button>
                        </div>
                    </Modal.Footer>
                </ModalBodyFooter>
            </Modal>
        );
    }

    return (
        <Modal show={show} onHide={() => handleClose(false)}>
            <Modal.Header><h2>{mode === 'create' ? 'New Payment' : 'Edit Payment'}</h2></Modal.Header>
            <ModalBodyFooter>
                <Modal.Body>
                    <div>Reminder: You are currently <u>{mode === 'create' ? 'creating' : 'editing'}</u> a payment.</div>
                    <br />

                    <div className="flex flex-row gap-x-4">
                        <div className="grid grid-cols-1 gap-y-2 w-full">
                            <h4>Select Student</h4>
                            <SelectSingle
                                id="payment-form-student"
                                name="student"
                                value={formik.values.student}
                                onChange={(e) => {
                                    formik.handleChange(e);
                                    // Manually trigger contract fetch after formik updates
                                    const selectedOption = e.target.value;
                                    if (selectedOption && selectedOption.value !== -1) {
                                        setTimeout(() => {
                                            fetchContractsForStudent(selectedOption.value, formik.values.onlyShowActiveAndExtra);
                                        }, 0);
                                    }
                                }}
                                options={studentOptions}
                            />
                            {formik.errors.student &&
                                <ErrorMessage color="mpLRed">{formik.errors.student}</ErrorMessage>
                            }
                            {fetching && formik.values.student?.value !== -1 &&
                                <div className="text-mpBlue">Loading contracts...</div>
                            }
                            <Check
                                id="payment-form-only-show-active-extra"
                                name="onlyShowActiveAndExtra"
                                label="Only show Active and Extra contracts"
                                color="mpLRed"
                                checked={formik.values.onlyShowActiveAndExtra}
                                onChange={(e) => {
                                    formik.handleChange(e);
                                    // Re-fetch contracts when filter changes
                                    if (formik.values.student && formik.values.student.value !== -1) {
                                        fetchContractsForStudent(formik.values.student.value, e.target.checked);
                                    }
                                }}
                            />
                            <br />
                            <h4>Linked Contract</h4>
                            <SelectSingle
                                id="payment-form-contract"
                                name="contract"
                                value={formik.values.contract}
                                onChange={formik.handleChange}
                                options={contractOptions.length ? contractOptions : [contractOption || defaultContractOption]}
                                disabled={!formik.values.student || formik.values.student.value === -1 || fetching}
                            />
                            <div>★ Denotes the current/default contract for this student</div>
                            {formik.values.student && formik.values.student.value !== -1 && contractOptions.length === 0 && hasFetched &&
                                <div className="text-mpOrange">No contracts found for this student.</div>
                            }
                            {formik.errors.contract &&
                                <ErrorMessage color="mpLRed">{formik.errors.contract}</ErrorMessage>
                            }
                        </div>
                    </div>

                    <br />
                    <hr />
                    <br />

                    <div className="flex flex-row gap-x-4">
                        <div className="grid grid-cols-1 gap-y-2">
                            <h4>Payment Date</h4>
                            <DatePicker
                                id="payment-form-date"
                                name="date"
                                value={formik.values.date}
                                onChange={formik.handleChange}
                            />
                            {formik.errors.date &&
                                <ErrorMessage color="mpLRed">{formik.errors.date}</ErrorMessage>
                            }
                        </div>
                    </div>

                    <br />

                    <div className="flex flex-row gap-x-4 items-start">
                        <div className="grid grid-cols-1 gap-y-2 w-1/2">
                            <h4>Method</h4>
                            <SelectSingle
                                id="payment-form-method"
                                name="method"
                                value={formik.values.method}
                                onChange={formik.handleChange}
                                options={methodOptions}
                            />
                            {formik.errors.method &&
                                <ErrorMessage color="mpLRed">{formik.errors.method}</ErrorMessage>
                            }
                        </div>
                        <div className="grid grid-cols-1 gap-y-2 w-1/2">
                            <h4>Amount</h4>
                            <FormikControl
                                id="payment-form-amount"
                                name="amount"
                                value={formik.values.amount}
                                onChange={formik.handleChange}
                                placeholder="Min: -30000, Max: 30000"
                            />
                            {formik.errors.amount &&
                                <ErrorMessage color="mpLRed">{formik.errors.amount}</ErrorMessage>
                            }
                        </div>
                    </div>

                    <br />

                    <div className="flex flex-row gap-x-4">
                        <div className="grid grid-cols-1 gap-y-2 w-full">
                            <h4>Notes</h4>
                            <FormikControl
                                id="payment-form-notes"
                                name="notes"
                                as="textarea"
                                value={formik.values.notes}
                                onChange={formik.handleChange}
                                placeholder="Max. 500 characters"
                            />
                            {formik.errors.notes &&
                                <ErrorMessage color="mpLRed">{formik.errors.notes}</ErrorMessage>
                            }
                        </div>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <div className="flex flex-row gap-x-2 flex-wrap">
                        {!formik.isValid && parseInt(formik.submitCount) && !formik.isSubmitting &&
                            <div className="text-mpLRed">One or more fields needs to be corrected.</div>
                        }
                        {formik.isSubmitting && <ModalProcessing />}
                        {formik.status && !formik.isSubmitting &&
                            <div className="text-mpLRed">{formik.status}</div>
                        }
                        <Button color="lte-mpLRed" disabled={formik.isSubmitting} onClick={handleClose}>Close</Button>
                        <Button color="lte-mpLBlue" onClick={() => formik.handleSubmit(formik)}>Submit</Button>
                    </div>
                </Modal.Footer>
            </ModalBodyFooter>
        </Modal>
    );
}

export default connect(null, {
    fetchContractsIds,
    fetchPaymentsIds,
    fetchContractsStudentNames,
    fetchContractsStudentIds,
    createPayment,
    updatePayment,
    deletePayment
})(MathPlexPaymentsModal);

